import React from 'react';
import styled from 'styled-components';

import { truncateText } from './utils/mixins';
import { useSidebarCtx } from './SidebarContext';

// ============================================================
//     TYPES
// ============================================================
interface ContentProps extends React.ComponentPropsWithoutRef<'div'> {}

// ============================================================
//     STYLES
// ============================================================
const StyledContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--cl-space-4)',
  flex: '0 0 auto',

  width: '100%',
  paddingBlock: 'var(--cl-space-1)',
  paddingInline: 'var(--cl-space-4)',

  ...truncateText,
  overflow: 'clip',

  transitionProperty: 'width',
  transitionDuration: 'var(--cl-duration-300)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',
});

const StyledBody = styled(StyledContent)({
  flex: '1 1 auto',
  minHeight: '100%',
  paddingBlock: 'var(--cl-space-3)',
});

/**
 * Stand-in for the Frontend-Platform `ScrollArea` atom (no component-library
 * equivalent exists): a plain vertical scroll container with a thin scrollbar.
 */
const StyledScrollWrapper = styled.div({
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'thin',

  '&::-webkit-scrollbar': {
    width: 6,
  },

  '&::-webkit-scrollbar-thumb': {
    background: 'var(--cl-gray-400)',
    borderRadius: 'var(--cl-radius-circle)',
  },

  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
});

// ============================================================
//     COMPONENTS
// ============================================================
/**
 * Fixed top section of the sidebar for persistent header content.
 *
 * Typically used for logos, account switchers, or primary navigation controls that should remain visible regardless of scroll position.
 */
export const Header = React.forwardRef<HTMLDivElement, ContentProps>((props, forwardedRef) => {
  const { isExpanded } = useSidebarCtx();
  return <StyledContent {...props} data-component="Sidebar.Header" data-expanded={isExpanded} ref={forwardedRef} />;
});

Header.displayName = 'Sidebar.Header' as const;

/**
 * Fixed bottom section of the sidebar for persistent footer content.
 *
 * Typically used for user account controls, settings links, or secondary actions that should remain visible regardless of scroll position.
 */
export const Footer = React.forwardRef<HTMLDivElement, ContentProps>((props, forwardedRef) => {
  const { isExpanded } = useSidebarCtx();
  return <StyledContent {...props} data-component="Sidebar.Footer" data-expanded={isExpanded} ref={forwardedRef} />;
});

Footer.displayName = 'Sidebar.Footer' as const;

/**
 * Scrollable main content area of the sidebar.
 *
 * Wraps content in a vertical scroll container.
 * Use this for the primary navigation content that may overflow the viewport.
 */
export const Body = React.forwardRef<HTMLDivElement, ContentProps>((props, forwardedRef) => {
  const { children, ...restProps } = props;
  const { isExpanded } = useSidebarCtx();
  return (
    <StyledScrollWrapper>
      <StyledBody {...restProps} data-component="Sidebar.Body" data-expanded={isExpanded} ref={forwardedRef}>
        {children}
      </StyledBody>
    </StyledScrollWrapper>
  );
});

Body.displayName = 'Sidebar.Body' as const;
